<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {computed, ref} from "vue";
import SearchField from "@/components/forms/SearchField.vue";
import {DataTableHeader, useDisplay} from "vuetify/framework";
import SortField from "@/components/forms/SortField.vue";
import {Pagination, PaginationFilters, TeamSummary} from "@/types";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import IconCountTooltip from "@/components/tooltips/IconCountTooltip.vue";
import TitleAvatar from "@/components/avatars/TitleAvatar.vue";
import NavigationTabWithChip from "@/components/tabs/NavigationTabWithChip.vue";
import RoleChip from "@/components/chips/RoleChip.vue";

defineOptions({layout: BaseLayout})

const {mobile} = useDisplay()

interface Props {
    title: string
    items: Pagination<TeamSummary>
    filters: PaginationFilters
}

const props = defineProps<Props>();

const search = ref<string|null>(props.filters.search);
const sortBy = ref<string>(props.filters.sort_by);
const sortDir = ref<'asc' | 'desc'>(props.filters.sort_dir);
const page = ref<number>(props.filters.page);

const tab = ref<'owned'>('owned');

const lastPage = computed(() => {
    return props.items.last_page || 1;
});

const sortItems: Array<{ title: string; value: string }> = [
    {
        title: 'Name',
        value: 'name'
    },
    {
        title: 'Created date',
        value: 'created_at'
    },
    {
        title: 'Updated date',
        value: 'updated_at'
    },
    {
        title: 'Members',
        value: 'users_count'
    }
]

const headers: Array<DataTableHeader> = [
    {
        title: 'Name',
        value: 'name',
        sortable: false,
        align: 'start',
        width: '80%'
    },
    {
        title: 'Actions',
        value: 'actions',
        sortable: false,
        align: 'end',
        width: '20%'
    },
]

const loadItems = () => {
    const params: Record<string, string|null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
        page: page.value.toString()
    };

    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    const uri = route('teams.index');

    router.get(
        uri,
        params,
        {
            preserveState: true,
        }
    );
}

const goToCategory = () => {
    // Keep the current search and sort parameters
    const params: Record<string, string|null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
        page: '1' // Reset to the first page
    }
    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    router.get(
        route('teams.index'),
        params,
        {
            preserveState: true,
        }
    );
};

const goToTeam = (id: number) => {
    router.get(route('teams.show', {team: id}));
};

const createNewTeam = () => {
    router.get(route('teams.create'))
};

</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between">
            <h1 class="text-h4 font-weight-bold">
                {{ props.title }}
            </h1>
            <v-btn
                :class="mobile ? 'mt-3' : ''"
                :block="mobile"
                @click="createNewTeam"
            >
                New resource
            </v-btn>
        </v-row>
        <v-row class="mt-10 mb-0">
            <v-col
                cols="12"
                class="pa-0"
            >
                <v-tabs
                    v-model="tab"
                    align-tabs="start"
                    color="primary"
                >
                    <navigation-tab-with-chip
                        :tab="tab"
                        value="owned"
                        :count="props.items.total"
                        @click="goToCategory"
                    >
                        My resources
                    </navigation-tab-with-chip>
                </v-tabs>
            </v-col>
        </v-row>
        <v-row class="mt-0 bg-grey-lighten-5 border-t-sm border-b-sm">
            <v-col
                cols="12"
                md="9"
                class="py-4"
            >
                <search-field
                    v-model="search"
                    @click="loadItems"
                />
            </v-col>
            <v-col
                cols="12"
                md="3"
                class="py-4"
            >
                <sort-field
                    v-model:sort-by="sortBy"
                    v-model:sort-dir="sortDir"
                    :items="sortItems"
                    @update:sort-by="loadItems"
                    @update:sort-dir="loadItems"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col
                cols="12"
                class="py-0"
            >
                <v-data-table
                    :headers="headers"
                    :items="props.items.data"
                    :items-per-page="props.items.per_page"
                    :page="props.items.current_page"
                    disable-sort
                    hide-default-header
                    hide-default-footer
                    no-data-text="No results found."
                >
                    <template #no-data>
                        <v-sheet class="mt-10">
                            <v-row
                                class="fill-height"
                                align="center"
                                justify="center"
                            >
                                <v-col
                                    cols="12"
                                    class="text-center"
                                >
                                    <v-avatar size="128">
                                        <v-icon
                                            size="128"
                                            color="grey-lighten-2"
                                        >
                                            mdi-emoticon-sad-outline
                                        </v-icon>
                                    </v-avatar>
                                    <h2 class="text-h5 font-weight-bold">
                                        No resources found.
                                    </h2>
                                    <p>
                                        Try adjusting your search or category criteria.
                                    </p>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </template>
                    <template #[`item.name`]="{ item }">
                        <v-list-item>
                            <template #prepend>
                                <title-avatar
                                    :id="item.id"
                                    :title="item.short_name"
                                    @click="goToTeam(item.id)"
                                />
                            </template>
                            <v-list-item-title
                                class="text-body-2 font-weight-bold"
                            >
                                <span
                                    class="custom-link"
                                    @click="goToTeam(item.id)"
                                >
                                    {{ item.name }}
                                </span>
                                <role-chip
                                    v-if="item.role"
                                    class="ml-2"
                                >
                                    {{ item.role }}
                                </role-chip>
                            </v-list-item-title>
                        </v-list-item>
                    </template>
                    <template #[`item.actions`]="{ item }">
                        <v-row
                            v-if="!mobile"
                            no-gutters
                            justify="space-evenly"
                            dense
                        >
                            <v-col cols="4">
                                <icon-count-tooltip
                                    icon="mdi-account-multiple-outline"
                                    :count="item.users_count"
                                >
                                    Members
                                </icon-count-tooltip>
                            </v-col>
                        </v-row>
                    </template>
                </v-data-table>
                <v-row
                    v-if="items.total > 0"
                    class="justify-center mt-5"
                >
                    <v-pagination
                        v-model="page"
                        :length="lastPage"
                        density="compact"
                        active-color="primary"
                        @update:model-value="loadItems"
                    />
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 42px;
}
</style>
